import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { isTickmillPartner } from '@utils/companyName.utils'

import { WalletDto, WalletTypeEnum } from '@model/WalletDto'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'

import { WalletTransferPage } from '../../Traders-Room/Wallets/WalletTransfer/WalletTransfer.page'

import styles from './IBWalletTransferPage.module.scss'

interface UseIBWalletTransferFetchValue {
  wallets: WalletDto[]
  isLoading: boolean
}

export const useIBWalletTransferFetch = (): UseIBWalletTransferFetchValue => {
  const apiClient = useApiClient(ClientApiClient)

  const { data = [], isLoading } = useQuery({
    queryKey: queryKeys.ib.walletTransferList(),
    queryFn: () => apiClient.getWallets(),
  })

  return { wallets: data, isLoading }
}

export const IBWalletTransferPage: React.FC = () => {
  useScrollToTop()
  const { account } = useAccountReadContext()

  const navigate = useNavigate()

  const { wallets, isLoading } = useIBWalletTransferFetch()

  const handleSubmit = () => {
    navigate('/dashboard/introducing-broker/reports/transactions-history')
  }

  const handlePageExitConfirmation = () => {
    navigate(-1)
  }

  const { t } = useTranslation()

  const ibOrPartnerTitle = isTickmillPartner(account)
    ? t('Wallet.Partner')
    : t('Wallet.Introducing Broker')

  return (
    <Loading isLoading={isLoading}>
      <WalletTransferPage
        limits={[]}
        title={`${ibOrPartnerTitle} ${t('Transfer')}`}
        description={
          <>
            <span className={styles.title}>{t('Wallet.Important!')}</span>{' '}
            {t('Wallet.You can transfer funds only to your main wallets (EUR, USD, GBP).')}
          </>
        }
        wallets={wallets}
        walletTypeFrom={WalletTypeEnum.IB}
        walletTypeTo={[WalletTypeEnum.IB, WalletTypeEnum.CFD]}
        onSuccess={handleSubmit}
        onCancel={handlePageExitConfirmation}
      />
    </Loading>
  )
}
